import React, { useState, useEffect, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  RefreshCw, AlertCircle, Globe, Server,
  ExternalLink, Clock, Users, UsersRound, Layout, Building2, Zap, CreditCard,
  Eye, EyeOff, Loader2, CheckCircle2, ChevronDown, Shield, Key, Trash2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import integrationsService from "../../services/integrationsService";
import WorkvivoDefaultSpaceCard from "./WorkvivoDefaultSpaceCard";
import WorkvivoNotificationMatrix from "./WorkvivoNotificationMatrix";
import WorkvivoInboundKudosToggle from "./WorkvivoInboundKudosToggle";
import WorkvivoSamlSsoCard from "./WorkvivoSamlSsoCard";
import organizationsService from "../../services/organizationsService";
import workvivoService from "../../services/workvivoService";
import ssoService from "../../services/ssoService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import TremendousConnect from "./TremendousConnect";
import ScimProvisioningSection from "./ScimProvisioningSection";
import AuthContext from "../../context/AuthContext";
import { useTranslation, useFormat } from "../../i18n";

const SUB_TABS = [
  { id: "identity", label: "integrations.tabs.identity", Icon: Shield },
  { id: "workvivo", label: "integrations.tabs.workvivo", Icon: Building2 },
  { id: "rewards", label: "integrations.tabs.rewards", Icon: CreditCard },
];

const IntegrationsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation("admin_integrations");
  const fmt = useFormat();
  const [subTab, setSubTab] = useState("identity");

  // Fetch organizations with Workvivo status
  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.list(),
  });

  // Superusers pick which organization the org-scoped cards below act on;
  // everyone else is pinned to their own. Defaults to the superuser's own org,
  // or the first one when they have none.
  const isSuperuser = !!user?.is_superuser;
  const [pickedOrgId, setPickedOrgId] = useState(null);
  const selectedOrgId = isSuperuser
    ? (pickedOrgId && organizations.some((o) => o.id === pickedOrgId)
        ? pickedOrgId
        : organizations.find((o) => o.id === user?.organization_id)?.id
          || organizations[0]?.id
          || null)
    : user?.organization_id || null;
  // Passed as `org_id` to the admin APIs; undefined keeps the JWT's org.
  const scopedOrgId = isSuperuser ? selectedOrgId || undefined : undefined;

  // Sync Workvivo users for specific org mutation (available for future use)
  // eslint-disable-next-line no-unused-vars
  const syncWorkvivoMutation = useMutation({
    mutationFn: (orgId) => integrationsService.syncWorkvivoUsers(orgId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(t("integrations.toasts.synced_users", { count: data.users_synced || 0 }));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Sync all Workvivo organizations mutation
  const syncAllWorkvivoMutation = useMutation({
    mutationFn: () => integrationsService.syncAllWorkvivoOrganizations(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(t("integrations.toasts.synced_orgs", { count: data.organizations_synced || 0 }));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Sync Workvivo team memberships mutation
  const syncTeamMembershipsMutation = useMutation({
    mutationFn: () => integrationsService.syncWorkvivoTeamMemberships(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(t("integrations.toasts.team_memberships_synced", { created: data.memberships_created || 0, updated: data.memberships_updated || 0 }));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // ---------------------------------------------------------------------------
  // SSO Configuration
  // ---------------------------------------------------------------------------
  const [showSSOModal, setShowSSOModal] = useState(false);
  const [ssoEditMode, setSSOEditMode] = useState(false);
  const [showSSOSecret, setShowSSOSecret] = useState(false);
  const [showSSODeleteConfirm, setShowSSODeleteConfirm] = useState(false);

  // Fetch SSO configuration
  const { data: ssoConfig, isLoading: ssoLoading, error: ssoError } = useQuery({
    queryKey: ["sso", "config", scopedOrgId || "self"],
    queryFn: () => ssoService.getConfig(scopedOrgId),
    enabled: !isSuperuser || !!scopedOrgId,
    retry: false,
  });

  const ssoConfigured = ssoConfig && !ssoError;

  // SSO form
  const {
    register: registerSSO,
    handleSubmit: handleSSOSubmit,
    reset: resetSSO,
    setValue: setSSOValue,
    watch: watchSSO,
    formState: { errors: ssoErrors }
  } = useForm({
    defaultValues: {
      provider_type: "azure_ad",
      client_id: "",
      client_secret: "",
      tenant_id: "",
      okta_domain: "",
      redirect_uri: `${window.location.origin}/api/v1/sso/callback`,
      is_enabled: true,
    }
  });

  const watchedProvider = watchSSO("provider_type");

  // Create SSO config mutation
  const createSSOMutation = useMutation({
    mutationFn: (data) => ssoService.createConfig(data, scopedOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success(t("integrations.toasts.sso_created"));
      closeSSOModal();
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Update SSO config mutation
  const updateSSOMutation = useMutation({
    mutationFn: (data) => ssoService.updateConfig(data, scopedOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success(t("integrations.toasts.sso_updated"));
      closeSSOModal();
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Delete SSO config mutation
  const deleteSSOMutation = useMutation({
    mutationFn: () => ssoService.deleteConfig(scopedOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success(t("integrations.toasts.sso_removed"));
      closeSSOModal();
      setShowSSODeleteConfirm(false);
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Toggle SSO enabled/disabled
  const toggleSSOMutation = useMutation({
    mutationFn: () => {
      if (!ssoConfig) return;
      return ssoService.updateConfig({
        provider_type: ssoConfig.provider_type,
        client_id: ssoConfig.client_id,
        client_secret: "", // backend keeps existing if empty
        tenant_id: ssoConfig.tenant_id || "",
        okta_domain: ssoConfig.okta_domain || "",
        redirect_uri: ssoConfig.redirect_uri,
        is_enabled: !ssoConfig.is_enabled,
      }, scopedOrgId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success(ssoConfig?.is_enabled ? t("integrations.toasts.sso_disabled") : t("integrations.toasts.sso_enabled"));
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  const openSSOModal = (edit = false) => {
    setSSOEditMode(edit);
    setShowSSOSecret(false);
    setShowSSODeleteConfirm(false);
    if (edit && ssoConfig) {
      resetSSO({
        provider_type: ssoConfig.provider_type,
        client_id: ssoConfig.client_id,
        client_secret: "",
        tenant_id: ssoConfig.tenant_id || "",
        okta_domain: ssoConfig.okta_domain || "",
        redirect_uri: ssoConfig.redirect_uri,
        is_enabled: ssoConfig.is_enabled,
      });
      // provider_type set via resetSSO
    } else {
      resetSSO({
        provider_type: "azure_ad",
        client_id: "",
        client_secret: "",
        tenant_id: "",
        okta_domain: "",
        redirect_uri: `${window.location.origin}/api/v1/sso/callback`,
        is_enabled: true,
      });
    }
    setShowSSOModal(true);
  };

  const closeSSOModal = () => {
    setShowSSOModal(false);
    setSSOEditMode(false);
    setShowSSOSecret(false);
    setShowSSODeleteConfirm(false);
    resetSSO();
  };

  const onSSOSubmit = (data) => {
    const payload = {
      provider_type: data.provider_type,
      client_id: data.client_id,
      client_secret: data.client_secret,
      tenant_id: data.provider_type === "azure_ad" ? data.tenant_id : null,
      okta_domain: data.provider_type === "okta" ? data.okta_domain : null,
      redirect_uri: data.redirect_uri,
      is_enabled: data.is_enabled,
    };
    if (ssoEditMode) {
      updateSSOMutation.mutate(payload);
    } else {
      createSSOMutation.mutate(payload);
    }
  };

  const maskValue = (val) => {
    if (!val || val.length <= 8) return val || "";
    return val.substring(0, 4) + "****" + val.substring(val.length - 4);
  };

  // Workvivo connect/resync state
  const [showResyncModal, setShowResyncModal] = useState(false);
  const [resyncOrg, setResyncOrg] = useState(null);
  const [resyncResult, setResyncResult] = useState(null);
  const [showConnectWorkvivo, setShowConnectWorkvivo] = useState(false);
  const [connectOrg, setConnectOrg] = useState(null);
  const [connectResult, setConnectResult] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [storedCredentials, setStoredCredentials] = useState(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [showUpdateCredentials, setShowUpdateCredentials] = useState(false);

  // Lock background scrolling while a modal is open
  const anyModalOpen = showSSOModal || showResyncModal || showConnectWorkvivo;
  useEffect(() => {
    if (!anyModalOpen) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [anyModalOpen]);

  // Resync form
  const {
    register: registerResync,
    handleSubmit: handleResyncSubmit,
    reset: resetResync,
    formState: { errors: resyncErrors }
  } = useForm({ defaultValues: { workvivo_base_url: "", workvivo_id: "", access_token: "" } });

  // Connect form
  const {
    register: registerConnect,
    handleSubmit: handleConnectSubmit,
    reset: resetConnect,
    formState: { errors: connectErrors }
  } = useForm({ defaultValues: { workvivo_base_url: "", workvivo_id: "", access_token: "" } });

  // Helper to extract setup result from SuccessResponse or raw response
  const extractResult = (response) => {
    // Service returns SuccessResponse: { message, data: { users_imported, ... } }
    // Or might return the inner data directly
    if (response?.data && typeof response.data === 'object' && 'users_imported' in response.data) {
      return response.data;
    }
    if (response && 'users_imported' in response) {
      return response;
    }
    // Fallback: return whatever we got, wrapped to ensure truthy
    return response?.data || response || { users_imported: 0, teams_imported: 0, spaces_imported: 0 };
  };

  // Resync mutation
  const resyncMutation = useMutation({
    mutationFn: ({ id, credentials }) => organizationsService.resyncWorkvivo(id, credentials),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      const result = extractResult(response);
      setResyncResult(result);
      toast.success(t("integrations.toasts.sync_complete", { users: result.users_imported || 0, teams: result.teams_imported || 0, spaces: result.spaces_imported || 0 }));
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Connect to Workvivo mutation
  const connectWorkvivoMutation = useMutation({
    mutationFn: (data) => workvivoService.completeSetup(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      const result = extractResult(response);
      setConnectResult(result);
      toast.success(t("integrations.toasts.connected", { users: result.users_imported || 0, teams: result.teams_imported || 0, spaces: result.spaces_imported || 0 }));
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  const openResyncModal = async (org) => {
    setResyncOrg(org);
    setResyncResult(null);
    resetResync({ workvivo_base_url: "", workvivo_id: "", access_token: "" });
    setShowResyncModal(true);
    setShowToken(false);
    setShowUpdateCredentials(false);
    setStoredCredentials(null);
    setCredentialsLoading(true);
    try {
      const creds = await organizationsService.getWorkvivoCredentials(org.id);
      setStoredCredentials(creds);
    } catch {
      // No stored credentials – user must enter them
      setShowUpdateCredentials(true);
    } finally {
      setCredentialsLoading(false);
    }
  };
  const closeResyncModal = () => { setShowResyncModal(false); setResyncOrg(null); setResyncResult(null); setShowToken(false); resetResync(); setStoredCredentials(null); setShowUpdateCredentials(false); };
  const onResyncSubmit = (data) => {
    // Only send credential fields that the user actually filled in (override stored)
    const credentials = {};
    if (data.workvivo_base_url) credentials.workvivo_base_url = data.workvivo_base_url;
    if (data.workvivo_id) credentials.workvivo_id = data.workvivo_id;
    if (data.access_token) credentials.access_token = data.access_token;
    resyncMutation.mutate({ id: resyncOrg.id, credentials });
  };
  const onQuickResync = () => {
    // One-click resync using stored credentials (no overrides)
    resyncMutation.mutate({ id: resyncOrg.id, credentials: {} });
  };

  const openConnectWorkvivo = (org) => {
    setConnectOrg(org);
    setConnectResult(null);
    resetConnect({ workvivo_base_url: "", workvivo_id: "", access_token: "" });
    setShowConnectWorkvivo(true);
    setShowToken(false);
  };
  const closeConnectWorkvivo = () => { setShowConnectWorkvivo(false); setConnectOrg(null); setConnectResult(null); setShowToken(false); resetConnect(); };
  const onConnectSubmit = (data) => {
    connectWorkvivoMutation.mutate({
      organization_id: connectOrg.id,
      workvivo_base_url: data.workvivo_base_url,
      workvivo_id: data.workvivo_id,
      access_token: data.access_token,
      import_users: true, import_teams: true, import_spaces: true, setup_bot: false,
    });
  };

  // Only the selected org (the user's own, for non-superusers).
  const visibleOrgs = organizations.filter(org => org.id === selectedOrgId);

  const workvivoConnectedOrgs = visibleOrgs.filter(org =>
    org.integration_type === 'workvivo' || org.import_stats?.workvivo_connected
  );

  // The count is emphasised inside the sentence: pick the plural form with
  // `count`, keep `{{n}}` unreplaced and split on it so the <span> survives.
  const usersDeactivatedParts = t("integrations.resync_modal.users_deactivated", { count: resyncResult?.users_deactivated || 0 }).split("{{n}}");
  const usersSkippedParts = t("integrations.resync_modal.users_skipped_inactive", { count: resyncResult?.users_skipped_inactive || 0 }).split("{{n}}");

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("integrations.title")}</h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">{t("integrations.subtitle")}</p>
      </div>

      {isSuperuser && organizations.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 sm:p-4 bg-slate-50 border border-slate-200 rounded-2xl">
          <label htmlFor="integrations-org-picker" className="flex items-center gap-2 text-sm font-medium text-slate-700 whitespace-nowrap">
            <Building2 className="w-4 h-4 text-slate-500" />
            {t("integrations.org_picker.label")}
          </label>
          <select
            id="integrations-org-picker"
            value={selectedOrgId || ""}
            onChange={(e) => setPickedOrgId(e.target.value)}
            className="w-full sm:max-w-sm px-3 py-2 border border-slate-300 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {[...organizations]
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
          </select>
          <p className="text-xs text-slate-500">{t("integrations.org_picker.hint")}</p>
        </div>
      )}

      {/* Sub-tabs. Local state, not a URL param: this section is also mounted
          by SuperAdminPage, and AdminPage drops every query param on tab
          change. */}
      <div className="flex flex-wrap gap-2">
        {SUB_TABS.map(({ id, label, Icon }) => {
          const isActive = subTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSubTab(id)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                  ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {t(label)}
            </button>
          );
        })}
      </div>

      {subTab === "identity" && (
        <>
        {/* SSO Section */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-2xl shadow-sm">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{t("integrations.sso.title")}</h2>
                  <p className="text-sm text-slate-600">{t("integrations.sso.subtitle")}</p>
                </div>
              </div>
              {ssoConfigured ? (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  ssoConfig.is_enabled
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                }`}>
                  {ssoConfig.is_enabled ? t("integrations.sso.status_enabled") : t("integrations.sso.status_disabled")}
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                  {t("integrations.sso.status_not_configured")}
                </span>
              )}
            </div>
          </div>

          <div className="p-6">
            {ssoLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            ) : ssoConfigured ? (
              <div className="space-y-4">
                {/* SSO Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Server className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{t("integrations.sso.provider")}</span>
                    </div>
                    <p className="text-sm text-slate-900">
                      {ssoConfig.provider_type === "azure_ad" ? "Azure AD" : "Okta"}
                    </p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{t("integrations.sso.client_id")}</span>
                    </div>
                    <p className="text-sm text-slate-900 font-mono">{maskValue(ssoConfig.client_id)}</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{t("integrations.sso.redirect_uri")}</span>
                    </div>
                    <p className="text-sm text-slate-900 font-mono truncate">{ssoConfig.redirect_uri}</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{t("integrations.sso.discovery_url")}</span>
                    </div>
                    <p className="text-sm text-slate-900 font-mono truncate">{ssoConfig.discovery_url}</p>
                  </div>
                </div>

                {/* SSO Actions */}
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => openSSOModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md"
                  >
                    {t("common:actions.edit")}
                  </button>
                  <button
                    onClick={() => toggleSSOMutation.mutate()}
                    disabled={toggleSSOMutation.isPending}
                    className={`px-4 py-2 border rounded-full transition-colors disabled:opacity-50 ${
                      ssoConfig.is_enabled
                        ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                        : "border-green-300 text-green-700 hover:bg-green-50"
                    }`}
                  >
                    {toggleSSOMutation.isPending ? t("integrations.sso.updating") : ssoConfig.is_enabled ? t("integrations.sso.disable") : t("integrations.sso.enable")}
                  </button>
                  <button
                    onClick={() => setShowSSODeleteConfirm(true)}
                    disabled={deleteSSOMutation.isPending}
                    className="px-4 py-2 border border-red-300 text-red-700 rounded-full hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {deleteSSOMutation.isPending ? t("integrations.sso.removing") : t("common:actions.remove")}
                  </button>
                </div>

                {/* Delete Confirmation */}
                {showSSODeleteConfirm && (
                  <div className="mt-2 p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <p className="text-sm text-red-800 mb-3">
                      {t("integrations.sso.delete_confirm")}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => deleteSSOMutation.mutate()}
                        disabled={deleteSSOMutation.isPending}
                        className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                      >
                        {deleteSSOMutation.isPending ? t("integrations.sso.removing") : t("integrations.sso.yes_remove")}
                      </button>
                      <button
                        onClick={() => setShowSSODeleteConfirm(false)}
                        className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
                      >
                        {t("common:actions.cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-600 mb-4">
                  {t("integrations.sso.empty_description")}
                </p>
                <button
                  onClick={() => openSSOModal(false)}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md font-medium"
                >
                  {t("integrations.sso.configure_button")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Authentication Mode Section */}
        {visibleOrgs.length > 0 && (() => {
          const currentOrg = visibleOrgs[0];
          const currentAuthMode = currentOrg?.auth_mode || "password";
          return (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white rounded-2xl shadow-sm">
                    <Key className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{t("integrations.auth_mode.title")}</h2>
                    <p className="text-sm text-slate-600">{t("integrations.auth_mode.subtitle")}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("integrations.auth_mode.label")}</label>
                  <select
                    value={currentAuthMode}
                    onChange={async (e) => {
                      const newMode = e.target.value;
                      if ((newMode === "sso" || newMode === "mixed") && !ssoConfigured) {
                        toast.error(t("integrations.toasts.configure_sso_first"));
                        return;
                      }
                      try {
                        await organizationsService.update(currentOrg.id, { auth_mode: newMode });
                        queryClient.invalidateQueries({ queryKey: ["organizations"] });
                        toast.success(t("integrations.toasts.auth_mode_updated"));
                      } catch (err) {
                        toast.error(formatError(err));
                      }
                    }}
                    className="w-full max-w-xs px-3 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="password">{t("integrations.auth_mode.password_only")}</option>
                    <option value="sso" disabled={!ssoConfigured}>{ssoConfigured ? t("integrations.auth_mode.sso_only") : t("integrations.auth_mode.sso_only_locked")}</option>
                    <option value="mixed" disabled={!ssoConfigured}>{ssoConfigured ? t("integrations.auth_mode.mixed") : t("integrations.auth_mode.mixed_locked")}</option>
                  </select>
                </div>
                <p className="text-sm text-slate-500">
                  {currentAuthMode === "password" && t("integrations.auth_mode.password_help")}
                  {currentAuthMode === "sso" && t("integrations.auth_mode.sso_help")}
                  {currentAuthMode === "mixed" && t("integrations.auth_mode.mixed_help")}
                </p>
              </div>
            </div>
          );
        })()}

        <WorkvivoSamlSsoCard key={selectedOrgId || "self"} orgId={scopedOrgId} />

        {/* SCIM Provisioning Section */}
        {selectedOrgId && (
          <ScimProvisioningSection key={selectedOrgId} organizationId={selectedOrgId} />
        )}
        </>
      )}

      {subTab === "workvivo" && (
        <>
        {/* Workvivo Section */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-2xl shadow-sm">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{t("integrations.workvivo.title")}</h2>
                  <p className="text-sm text-slate-600">{t("integrations.workvivo.subtitle")}</p>
                </div>
              </div>
              {workvivoConnectedOrgs.length > 0 && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {t("integrations.workvivo.connected_count", { count: workvivoConnectedOrgs.length })}
                </span>
              )}
            </div>
          </div>

          <div className="p-6">
            {orgsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : visibleOrgs.length > 0 ? (
              <div className="space-y-4">
                {/* Global Sync Buttons - only when there are connected orgs */}
                {workvivoConnectedOrgs.length > 0 && (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm text-slate-500 flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {t("integrations.workvivo.auto_sync_note")}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => syncTeamMembershipsMutation.mutate()}
                        disabled={syncTeamMembershipsMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Users className={`w-4 h-4 ${syncTeamMembershipsMutation.isPending ? "animate-spin" : ""}`} />
                        {syncTeamMembershipsMutation.isPending ? t("integrations.workvivo.syncing") : t("integrations.workvivo.sync_team_memberships")}
                      </button>
                      {user?.is_superuser && (
                        <button
                          onClick={() => syncAllWorkvivoMutation.mutate()}
                          disabled={syncAllWorkvivoMutation.isPending}
                          className="flex items-center gap-2 px-4 py-2 org-gradient text-white rounded-full transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Zap className={`w-4 h-4 ${syncAllWorkvivoMutation.isPending ? "animate-spin" : ""}`} />
                          {syncAllWorkvivoMutation.isPending ? t("integrations.workvivo.syncing_all") : t("integrations.workvivo.sync_all_organizations")}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Organizations Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t("integrations.workvivo.table.organization")}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t("integrations.workvivo.table.status")}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t("integrations.workvivo.table.import_stats")}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t("integrations.workvivo.table.last_sync")}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t("integrations.workvivo.table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {visibleOrgs.map((org) => {
                        const isWorkvivo = org.integration_type === 'workvivo' || org.import_stats?.workvivo_connected;
                        return (
                          <tr key={org.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Building2 className="w-5 h-5 text-slate-400 mr-2" />
                                <span className="text-sm font-medium text-slate-900">{org.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isWorkvivo ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  {t("integrations.workvivo.status_connected")}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                  {t("integrations.workvivo.status_not_connected")}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isWorkvivo ? (
                                <div className="flex items-center gap-3 text-xs text-slate-600">
                                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t("integrations.workvivo.stats_users", { count: org.import_stats?.imported_users || 0 })}</span>
                                  <span className="flex items-center gap-1"><UsersRound className="w-3 h-3" />{t("integrations.workvivo.stats_teams", { count: org.import_stats?.imported_teams || 0 })}</span>
                                  <span className="flex items-center gap-1"><Layout className="w-3 h-3" />{t("integrations.workvivo.stats_spaces", { count: org.import_stats?.total_spaces || 0 })}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                              {isWorkvivo && org.import_stats?.last_sync_at
                                ? fmt.dateTime(org.import_stats.last_sync_at)
                                : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isWorkvivo ? (
                                <button
                                  onClick={() => openResyncModal(org)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full hover:bg-green-100 transition-colors"
                                  title={t("integrations.workvivo.resync_title")}
                                >
                                  <RefreshCw className="w-3.5 h-3.5" />
                                  {t("integrations.workvivo.resync")}
                                </button>
                              ) : (
                                <button
                                  onClick={() => openConnectWorkvivo(org)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                                  title={t("integrations.workvivo.connect_title")}
                                >
                                  <Globe className="w-3.5 h-3.5" />
                                  {t("integrations.workvivo.connect")}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">{t("integrations.workvivo.no_orgs_title")}</p>
                    <p className="text-sm text-blue-800">{t("integrations.workvivo.no_orgs_description")}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <WorkvivoDefaultSpaceCard key={selectedOrgId || "self"} orgId={scopedOrgId} />
            </div>

            <div className="mt-6">
              <WorkvivoInboundKudosToggle
                key={selectedOrgId || "self"}
                orgId={scopedOrgId}
              />
            </div>

            <div className="mt-6">
              <WorkvivoNotificationMatrix
                key={selectedOrgId || "self"}
                orgId={scopedOrgId}
              />
            </div>
          </div>
        </div>
        </>
      )}

      {subTab === "rewards" && (
        <>
        {/* Tremendous Connect Section - Per Organization */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-2xl shadow-sm">
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{t("integrations.rewards.title")}</h2>
                  <p className="text-sm text-slate-600">{t("integrations.rewards.subtitle")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            {orgsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            ) : visibleOrgs.length > 0 ? (
              <div className="space-y-6">
                <p className="text-sm text-slate-600">
                  {t("integrations.rewards.description")}
                </p>
                {visibleOrgs.map((org) => (
                  <div key={org.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="font-medium text-slate-800">{org.name}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <TremendousConnect
                        organizationId={org.id}
                        adminEmail={user?.email || "admin@example.com"}
                        adminName={user?.full_name || user?.email?.split('@')[0] || "Admin User"}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl text-center">
                <p className="text-sm text-slate-600">{t("integrations.rewards.no_orgs")}</p>
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Modals stay mounted across sub-tabs so an open flow is not lost. */}
      {/* SSO Configure/Edit Modal */}
      <AnimatePresence>
        {showSSOModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">
                  {ssoEditMode ? t("integrations.sso.modal.edit_title") : t("integrations.sso.modal.create_title")}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {t("integrations.sso.modal.description")}
                </p>
              </div>

              <form onSubmit={handleSSOSubmit(onSSOSubmit)} className="p-6 space-y-5">
                {/* Provider Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("integrations.sso.modal.identity_provider")}</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { setSSOValue("provider_type", "azure_ad"); }}
                      className={`p-4 rounded-full border-2 text-center transition-all ${
                        watchedProvider === "azure_ad"
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-700"
                      }`}
                    >
                      <Shield className="w-8 h-8 mx-auto mb-2" />
                      <span className="text-sm font-medium">Azure AD</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSSOValue("provider_type", "okta"); }}
                      className={`p-4 rounded-full border-2 text-center transition-all ${
                        watchedProvider === "okta"
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-700"
                      }`}
                    >
                      <Key className="w-8 h-8 mx-auto mb-2" />
                      <span className="text-sm font-medium">Okta</span>
                    </button>
                  </div>
                  <input type="hidden" {...registerSSO("provider_type")} />
                </div>

                {/* Client ID */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.sso.client_id")}</label>
                  <input
                    type="text"
                    {...registerSSO("client_id", { required: t("integrations.sso.modal.client_id_required") })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder={t("integrations.sso.modal.client_id_placeholder")}
                  />
                  {ssoErrors.client_id && (
                    <p className="text-xs text-red-600 mt-1">{ssoErrors.client_id.message}</p>
                  )}
                </div>

                {/* Client Secret */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.sso.modal.client_secret")}</label>
                  <div className="relative">
                    <input
                      type={showSSOSecret ? "text" : "password"}
                      {...registerSSO("client_secret", { required: !ssoEditMode ? t("integrations.sso.modal.client_secret_required") : false })}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder={ssoEditMode ? t("integrations.sso.modal.client_secret_keep_placeholder") : t("integrations.sso.modal.client_secret_placeholder")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSSOSecret(!showSSOSecret)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      {showSSOSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {ssoEditMode && (
                    <p className="text-xs text-slate-500 mt-1">{t("integrations.sso.modal.client_secret_keep_hint")}</p>
                  )}
                  {ssoErrors.client_secret && (
                    <p className="text-xs text-red-600 mt-1">{ssoErrors.client_secret.message}</p>
                  )}
                </div>

                {/* Tenant ID (Azure AD) */}
                {watchedProvider === "azure_ad" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.sso.modal.tenant_id")}</label>
                    <input
                      type="text"
                      {...registerSSO("tenant_id", {
                        validate: (value) => watchedProvider === "azure_ad" && !value ? t("integrations.sso.modal.tenant_id_required") : true
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder={t("integrations.sso.modal.tenant_id_placeholder")}
                    />
                    {ssoErrors.tenant_id && (
                      <p className="text-xs text-red-600 mt-1">{ssoErrors.tenant_id.message}</p>
                    )}
                  </div>
                )}

                {/* Okta Domain */}
                {watchedProvider === "okta" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.sso.modal.okta_domain")}</label>
                    <input
                      type="text"
                      {...registerSSO("okta_domain", {
                        validate: (value) => watchedProvider === "okta" && !value ? t("integrations.sso.modal.okta_domain_required") : true
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder={t("integrations.sso.modal.okta_domain_placeholder")}
                    />
                    {ssoErrors.okta_domain && (
                      <p className="text-xs text-red-600 mt-1">{ssoErrors.okta_domain.message}</p>
                    )}
                  </div>
                )}

                {/* Redirect URI (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.sso.redirect_uri")}</label>
                  <input
                    type="text"
                    {...registerSSO("redirect_uri", { required: t("integrations.sso.modal.redirect_uri_required") })}
                    placeholder="https://your-domain.com/api/v1/sso/callback"
                    className="w-full px-3 py-2 border border-slate-200 rounded-full bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t("integrations.sso.modal.redirect_uri_hint")}</p>
                </div>

                {/* Enable SSO */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sso-enabled"
                    {...registerSSO("is_enabled")}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                  />
                  <label htmlFor="sso-enabled" className="text-sm text-slate-700">{t("integrations.sso.modal.enable_sso")}</label>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <div>
                    {ssoEditMode && (
                      <button
                        type="button"
                        onClick={() => setShowSSODeleteConfirm(true)}
                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t("integrations.sso.modal.delete_configuration")}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closeSSOModal}
                      className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={createSSOMutation.isPending || updateSSOMutation.isPending}
                      className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md text-sm font-medium disabled:opacity-50"
                    >
                      {(createSSOMutation.isPending || updateSSOMutation.isPending) ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t("common:actions.saving")}
                        </span>
                      ) : (
                        ssoEditMode ? t("common:actions.update") : t("common:actions.save")
                      )}
                    </button>
                  </div>
                </div>

                {/* Delete confirmation inside modal */}
                {showSSODeleteConfirm && ssoEditMode && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <p className="text-sm text-red-800 mb-3">
                      {t("integrations.sso.modal.delete_confirm")}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => deleteSSOMutation.mutate()}
                        disabled={deleteSSOMutation.isPending}
                        className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                      >
                        {deleteSSOMutation.isPending ? t("integrations.sso.removing") : t("integrations.sso.yes_remove")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSSODeleteConfirm(false)}
                        className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
                      >
                        {t("common:actions.cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Connect to Workvivo Modal */}
      <AnimatePresence>
        {showConnectWorkvivo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={closeConnectWorkvivo} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{t("integrations.connect_modal.title")}</h2>
                    <p className="text-sm text-slate-500">{connectOrg?.name || t("integrations.connect_modal.organization_fallback")}</p>
                  </div>
                </div>
              </div>

              {connectResult ? (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">{t("integrations.connect_modal.connected_title")}</h3>
                    <p className="text-sm text-slate-500">{t("integrations.connect_modal.connected_subtitle")}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{fmt.number(connectResult.users_imported || 0)}</p>
                      <p className="text-xs text-blue-600">{t("integrations.connect_modal.users")}</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <UsersRound className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-700">{fmt.number(connectResult.teams_imported || 0)}</p>
                      <p className="text-xs text-purple-600">{t("integrations.connect_modal.teams")}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                      <Layout className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-emerald-700">{fmt.number(connectResult.spaces_imported || 0)}</p>
                      <p className="text-xs text-emerald-600">{t("integrations.connect_modal.spaces")}</p>
                    </div>
                  </div>
                  <button onClick={closeConnectWorkvivo} className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all">{t("common:actions.done")}</button>
                </div>
              ) : (
                <form onSubmit={handleConnectSubmit(onConnectSubmit)} className="p-6 space-y-4">
                  <p className="text-sm text-slate-600 mb-4">{t("integrations.connect_modal.intro")}</p>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.connect_modal.base_url")} <span className="text-red-500">*</span></label>
                    <input type="url" {...registerConnect("workvivo_base_url", { required: t("integrations.connect_modal.base_url_required") })} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="https://api.workvivo.com" />
                    {connectErrors.workvivo_base_url && <p className="text-xs text-red-500 mt-1">{connectErrors.workvivo_base_url.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.connect_modal.workvivo_id")} <span className="text-red-500">*</span></label>
                    <input type="text" {...registerConnect("workvivo_id", { required: t("integrations.connect_modal.workvivo_id_required") })} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("integrations.connect_modal.workvivo_id_placeholder")} />
                    {connectErrors.workvivo_id && <p className="text-xs text-red-500 mt-1">{connectErrors.workvivo_id.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.connect_modal.access_token")} <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type={showToken ? "text" : "password"} {...registerConnect("access_token", { required: t("integrations.connect_modal.access_token_required") })} className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t("integrations.connect_modal.access_token_placeholder")} />
                      <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                        {showToken ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-slate-600" />}
                      </button>
                    </div>
                    {connectErrors.access_token && <p className="text-xs text-red-500 mt-1">{connectErrors.access_token.message}</p>}
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={closeConnectWorkvivo} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">{t("common:actions.cancel")}</button>
                    <button type="submit" disabled={connectWorkvivoMutation.isPending} className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {connectWorkvivoMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />{t("integrations.connect_modal.connecting")}</>) : (<><Globe className="w-4 h-4" />{t("integrations.connect_modal.connect")}</>)}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Resync from Workvivo Modal */}
      <AnimatePresence>
        {showResyncModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={closeResyncModal} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{t("integrations.resync_modal.title")}</h2>
                    <p className="text-sm text-slate-500">{resyncOrg?.name || t("integrations.connect_modal.organization_fallback")}</p>
                  </div>
                </div>
              </div>

              {resyncResult ? (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">{t("integrations.resync_modal.complete_title")}</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{fmt.number(resyncResult.users_imported || 0)}</p>
                      <p className="text-xs text-blue-600">{t("integrations.connect_modal.users")}</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{fmt.number(resyncResult.teams_imported || 0)}</p>
                      <p className="text-xs text-purple-600">{t("integrations.connect_modal.teams")}</p>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{fmt.number(resyncResult.spaces_imported || 0)}</p>
                      <p className="text-xs text-green-600">{t("integrations.connect_modal.spaces")}</p>
                    </div>
                  </div>
                  {resyncResult.users_deactivated > 0 && (
                    <div className="bg-amber-50 rounded-2xl p-3 mb-4 text-center">
                      <p className="text-sm text-amber-700">{usersDeactivatedParts[0]}<span className="font-semibold">{fmt.number(resyncResult.users_deactivated)}</span>{usersDeactivatedParts[1]}</p>
                    </div>
                  )}
                  {resyncResult.users_skipped_inactive > 0 && (
                    <div className="bg-gray-50 rounded-2xl p-3 mb-4 text-center">
                      <p className="text-sm text-gray-600">{usersSkippedParts[0]}<span className="font-semibold">{fmt.number(resyncResult.users_skipped_inactive)}</span>{usersSkippedParts[1]}</p>
                    </div>
                  )}
                  {resyncResult.errors && resyncResult.errors.length > 0 && (
                    <div className="bg-red-50 rounded-2xl p-3 mb-4">
                      <p className="text-sm font-medium text-red-700 mb-1">{t("integrations.resync_modal.errors_label")}</p>
                      <ul className="text-xs text-red-600 list-disc list-inside">
                        {resyncResult.errors.slice(0, 3).map((err, i) => (<li key={i}>{err}</li>))}
                      </ul>
                    </div>
                  )}
                  <button onClick={closeResyncModal} className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all">{t("common:actions.done")}</button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  {credentialsLoading ? (
                    <div className="flex items-center justify-center min-h-[240px]">
                      <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Stored credentials info */}
                      {storedCredentials && (
                        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">{t("integrations.resync_modal.stored_credentials")}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-500">{t("integrations.resync_modal.base_url")}</span>
                              <p className="font-mono text-slate-800 text-xs truncate">{storedCredentials.base_url}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">{t("integrations.resync_modal.workvivo_id")}</span>
                              <p className="font-mono text-slate-800 text-xs truncate">{storedCredentials.workvivo_id}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">{t("integrations.resync_modal.access_token")}</span>
                              <p className="font-mono text-slate-800 text-xs">{storedCredentials.has_token ? storedCredentials.token_hint : t("integrations.resync_modal.token_not_set")}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {!storedCredentials && !credentialsLoading && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm text-amber-800">{t("integrations.resync_modal.no_stored_credentials")}</span>
                          </div>
                        </div>
                      )}

                      {/* Collapsible Update Credentials section */}
                      <div className="border border-slate-200 rounded-2xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowUpdateCredentials(!showUpdateCredentials)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                        >
                          <span className="text-sm font-medium text-slate-700">
                            {storedCredentials ? t("integrations.resync_modal.update_credentials") : t("integrations.resync_modal.enter_credentials")}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showUpdateCredentials ? "rotate-180" : ""}`} />
                        </button>
                        {showUpdateCredentials && (
                          <form onSubmit={handleResyncSubmit(onResyncSubmit)} className="p-4 space-y-3 border-t border-slate-200">
                            <p className="text-xs text-slate-500">
                              {storedCredentials
                                ? t("integrations.resync_modal.keep_stored_hint")
                                : t("integrations.resync_modal.enter_credentials_hint")}
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.resync_modal.base_url_label")}</label>
                              <input type="url" {...registerResync("workvivo_base_url")} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={storedCredentials?.base_url || "https://api.workvivo.com"} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.resync_modal.workvivo_id")}</label>
                              <input type="text" {...registerResync("workvivo_id")} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={storedCredentials?.workvivo_id || t("integrations.resync_modal.workvivo_id_placeholder")} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">{t("integrations.resync_modal.access_token")}</label>
                              <div className="relative">
                                <input type={showToken ? "text" : "password"} {...registerResync("access_token")} className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={storedCredentials?.has_token ? t("integrations.resync_modal.token_keep_placeholder") : t("integrations.resync_modal.token_placeholder")} />
                                <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                                  {showToken ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-slate-600" />}
                                </button>
                              </div>
                            </div>
                            <button type="submit" disabled={resyncMutation.isPending} className="w-full px-4 py-2 org-gradient text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                              {resyncMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />{t("integrations.resync_modal.syncing")}</>) : (<><RefreshCw className="w-4 h-4" />{t("integrations.resync_modal.update_and_sync")}</>)}
                            </button>
                          </form>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-2">
                        <button type="button" onClick={closeResyncModal} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">{t("common:actions.cancel")}</button>
                        {storedCredentials && (
                          <button type="button" onClick={onQuickResync} disabled={resyncMutation.isPending} className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            {resyncMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />{t("integrations.resync_modal.syncing")}</>) : (<><RefreshCw className="w-4 h-4" />{t("integrations.resync_modal.start_sync")}</>)}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntegrationsSection;
